import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import adminApi from './api'
import { MdCloudUpload, MdClose, MdPhotoLibrary } from 'react-icons/md'
import { Notification } from './components/Notification'
import { LocationPickerWithModal } from './components/LocationPickerWithModal'
import { findRegionCodeByCoords } from '../utils/regionByCoords'
import { ConfirmModal } from './components/ConfirmModal'
import { API_URL } from '../api/base'

const MAX_FILE_SIZE_MB = 10

interface EventLocationDraft {
  key: string           // локальный ключ для React (не сохраняется)
  id?: number           // серверный id, если локация уже существует
  nameRu: string
  nameKy: string
  nameEn: string
  latitude: string
  longitude: string
  regionId: string
}

interface EventFormData {
  name: string
  description: string
  locations: EventLocationDraft[]
  eventDate: string
  eventTime: string
  collectFrom: string
  collectTo: string
  targetTrees: string
  targetAmount: string
  status: 'planned' | 'active' | 'finished'
  isActiveDefault: boolean
  participantsCount: string
  areaHa: string
  seedlingTypes: string
}

const makeLocationKey = () =>
  `loc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const emptyLocation = (): EventLocationDraft => ({
  key: makeLocationKey(),
  nameRu: '',
  nameKy: '',
  nameEn: '',
  latitude: '',
  longitude: '',
  regionId: ''
})

interface RegionOption {
  id: number
  code: string
  name: { ru?: string; ky?: string; en?: string }
}

interface MediaCaption {
  ru?: string
  ky?: string
  en?: string
}

interface MediaFile {
  id: number
  event_id: number
  type: 'image' | 'video'
  url: string
  thumbnail: string | null
  caption: string | MediaCaption | null
  display_order: number
  created_at: string
}

// Функция для транслитерации кириллицы в латиницу
function transliterate(text: string): string {
  const map: Record<string, string> = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
    'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'Yo',
    'Ж': 'Zh', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
    'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
    'Ф': 'F', 'Х': 'H', 'Ц': 'Ts', 'Ч': 'Ch', 'Ш': 'Sh', 'Щ': 'Sch',
    'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya'
  }

  return text
    .split('')
    .map(char => map[char] || char)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// Генерация slug из названия
function generateSlug(name: string): string {
  return transliterate(name)
}

export const EventForm: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null)
  const [userRole, setUserRole] = useState<string>('')
  const [totalEventsCount, setTotalEventsCount] = useState<number>(0)

  const [formData, setFormData] = useState<EventFormData>({
    name: '',
    description: '',
    locations: [],
    eventDate: '',
    eventTime: '09:00',
    collectFrom: '',
    collectTo: '',
    targetTrees: '',
    targetAmount: '',
    status: 'planned',
    isActiveDefault: false,
    participantsCount: '',
    areaHa: '',
    seedlingTypes: ''
  })
  const [draggedLocationIdx, setDraggedLocationIdx] = useState<number | null>(null)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [regions, setRegions] = useState<RegionOption[]>([])

  // Партнёры события
  interface CompanyOption { id: number; name: string; logo_url: string | null }
  interface AttachedCompany { id: number; company_id: number; name: string; logo_url: string | null }
  const [allCompanies, setAllCompanies] = useState<CompanyOption[]>([])
  const [attached, setAttached] = useState<AttachedCompany[]>([])
  const [partnerPickerOpen, setPartnerPickerOpen] = useState(false)
  const [partnerPickerSearch, setPartnerPickerSearch] = useState('')
  const [partnerPickerTempIds, setPartnerPickerTempIds] = useState<Set<number>>(new Set())
  const [partnersBusy, setPartnersBusy] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get<{ regions: RegionOption[] }>('/api/regions')
        setRegions(data.regions || [])
      } catch (err) {
        console.error('Failed to load regions:', err)
      }
    })()
  }, [])

  // Загрузка всех партнёров (для попапа multi-select)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get<{ companies: CompanyOption[] }>('/api/admin/companies')
        setAllCompanies(data.companies || [])
      } catch (err) {
        console.error('Failed to load companies:', err)
      }
    })()
  }, [])

  // Загрузка привязанных к событию партнёров (только при редактировании)
  const fetchAttached = async (eventId: string | number) => {
    try {
      const { data } = await adminApi.get<{ companies: AttachedCompany[] }>(`/api/admin/companies/events/${eventId}/companies`)
      setAttached(data.companies || [])
    } catch (err) {
      console.error('Failed to load attached partners:', err)
    }
  }

  useEffect(() => {
    if (id) fetchAttached(id)
  }, [id])

  // ==================== Locations helpers ====================
  const updateLocation = (idx: number, patch: Partial<EventLocationDraft>) => {
    setFormData(prev => {
      const next = [...prev.locations]
      next[idx] = { ...next[idx], ...patch }
      return { ...prev, locations: next }
    })
  }

  const addLocation = () => {
    setFormData(prev => ({ ...prev, locations: [...prev.locations, emptyLocation()] }))
  }

  const removeLocation = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== idx)
    }))
  }

  const handleLocationDragStart = (idx: number) => setDraggedLocationIdx(idx)
  const handleLocationDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    if (draggedLocationIdx == null || draggedLocationIdx === idx) return
    setFormData(prev => {
      const next = [...prev.locations]
      const [moved] = next.splice(draggedLocationIdx, 1)
      next.splice(idx, 0, moved)
      return { ...prev, locations: next }
    })
    setDraggedLocationIdx(idx)
  }
  const handleLocationDragEnd = () => setDraggedLocationIdx(null)

  const detachPartner = async (attachedId: number) => {
    setPartnersBusy(true)
    try {
      await adminApi.delete(`/api/admin/companies/event-companies/${attachedId}`)
      setAttached(prev => prev.filter(a => a.id !== attachedId))
    } catch {
      setNotification({ message: 'Не удалось отвязать партнёра', type: 'error' })
    } finally {
      setPartnersBusy(false)
    }
  }

  const openPartnerPicker = () => {
    setPartnerPickerTempIds(new Set(attached.map(a => a.company_id)))
    setPartnerPickerSearch('')
    setPartnerPickerOpen(true)
  }

  const savePartnerPicker = async () => {
    if (!id) {
      setNotification({ message: 'Сначала сохраните событие', type: 'error' })
      return
    }
    setPartnersBusy(true)
    try {
      const currentIds = new Set(attached.map(a => a.company_id))
      const toAdd = Array.from(partnerPickerTempIds).filter(cid => !currentIds.has(cid))
      const toRemove = attached.filter(a => !partnerPickerTempIds.has(a.company_id))

      for (const cid of toAdd) {
        await adminApi.post(`/api/admin/companies/events/${id}/companies`, { companyId: cid })
      }
      for (const a of toRemove) {
        await adminApi.delete(`/api/admin/companies/event-companies/${a.id}`)
      }
      await fetchAttached(id)
      setPartnerPickerOpen(false)
      setNotification({ message: 'Партнёры обновлены', type: 'success' })
    } catch {
      setNotification({ message: 'Ошибка при сохранении партнёров', type: 'error' })
    } finally {
      setPartnersBusy(false)
    }
  }

  // Media state
  const [existingMedia, setExistingMedia] = useState<MediaFile[]>([])
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [fileUrlMap, setFileUrlMap] = useState<Map<File, string>>(new Map())
  const [videoUrls, setVideoUrls] = useState<string[]>([''])
  const [isDragging, setIsDragging] = useState(false)
  const [draggedFileIndex, setDraggedFileIndex] = useState<number | null>(null)
  const [draggedMediaIndex, setDraggedMediaIndex] = useState<number | null>(null)
  const [modalImage, setModalImage] = useState<string | null>(null)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<{show: boolean, id: number | null}>({show: false, id: null})

  // Load user role from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        setUserRole(user.role || '')
      } catch (e) {
        // Failed to parse user data
      }
    }
  }, [])

  useEffect(() => {
    if (id) {
      fetchEvent(id)
    } else {
      // При создании нового события, загружаем количество существующих событий
      fetchEventsCount()
    }
  }, [id])

  // Загрузить количество событий
  const fetchEventsCount = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/events')
      const count = data.events.length
      setTotalEventsCount(count)

      // Если это первое событие, автоматически устанавливаем isActiveDefault
      if (count === 0) {
        setFormData(prev => ({ ...prev, isActiveDefault: true }))
      }
    } catch (err) {
      console.error('Error fetching events count:', err)
    }
  }

  // Cleanup для URL.createObjectURL при размонтировании
  useEffect(() => {
    const newUrlMap = new Map(fileUrlMap)
    let hasChanges = false

    // Revoke old URLs that are no longer in newFiles array
    const currentFiles = new Set(newFiles)
    fileUrlMap.forEach((url, file) => {
      if (!currentFiles.has(file)) {
        URL.revokeObjectURL(url)
        newUrlMap.delete(file)
        hasChanges = true
      }
    })

    // Create URLs for new files
    newFiles.forEach(file => {
      if (!newUrlMap.has(file)) {
        const url = URL.createObjectURL(file)
        newUrlMap.set(file, url)
        hasChanges = true
      }
    })

    // Update state if there were changes
    if (hasChanges) {
      setFileUrlMap(newUrlMap)
    }

    // Cleanup all URLs on unmount
    return () => {
      newUrlMap.forEach(url => URL.revokeObjectURL(url))
    }
  }, [newFiles])


  const fetchEvent = async (eventId: string) => {
    try {
      setLoading(true)
      const { data } = await adminApi.get(`/api/admin/events/${eventId}`)
      const event = data.event

      // Извлекаем время из eventDate (убираем Z для локальной интерпретации)
      const eventDateStr = event.eventDate.replace('Z', '');
      const eventDateTime = new Date(eventDateStr);
      const eventTimeStr = eventDateTime.toTimeString().slice(0, 5); // HH:MM

      const apiLocations: any[] = Array.isArray(event.locations) ? event.locations : []
      const locations: EventLocationDraft[] = apiLocations.map((loc: any) => ({
        key: makeLocationKey(),
        id: loc.id,
        nameRu: loc.name?.ru || '',
        nameKy: loc.name?.ky || loc.name?.kg || '',
        nameEn: loc.name?.en || '',
        latitude: loc.latitude != null ? String(loc.latitude) : '',
        longitude: loc.longitude != null ? String(loc.longitude) : '',
        regionId: loc.regionId != null ? String(loc.regionId) : ''
      }))

      setFormData({
        name: event.name,
        description: event.description,
        locations,
        eventDate: event.eventDate.split('T')[0],
        eventTime: eventTimeStr || '09:00',
        collectFrom: event.collectFrom ? event.collectFrom.split('T')[0] : '',
        collectTo: event.collectTo ? event.collectTo.split('T')[0] : '',
        targetTrees: event.targetTrees?.toString() || '',
        targetAmount: event.targetAmount?.toString() || '',
        status: event.status,
        isActiveDefault: event.isActiveDefault,
        participantsCount: event.participantsCount != null ? String(event.participantsCount) : '',
        areaHa: event.areaHa != null ? String(event.areaHa) : '',
        seedlingTypes: Array.isArray(event.seedlingTypes)
          ? event.seedlingTypes
              .map((s: any) => {
                const name = s?.name?.ru || s?.name?.en || s?.name?.ky || ''
                return s?.count ? `${name} — ${s.count}` : name
              })
              .filter(Boolean)
              .join('\n')
          : ''
      })

      // Load existing media
      if (data.media) {
        setExistingMedia(data.media)
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка загрузки события')
        : 'Ошибка загрузки события'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Валидация
    const errors: Record<string, string> = {}
    if (!formData.name.trim()) errors.name = 'Обязательное поле'
    if (!formData.description.trim()) errors.description = 'Обязательное поле'
    if (!formData.eventDate) errors.eventDate = 'Обязательное поле'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)

      let errorMessage = 'Заполните обязательные поля: '
      const errorFields = []
      if (errors.name) errorFields.push('Название')
      if (errors.description) errorFields.push('Описание')
      if (errors.eventDate) errorFields.push('Дата события')
      errorMessage += errorFields.join(', ')

      setNotification({ message: errorMessage, type: 'error' })
      return
    }

    setLoading(true)

    try {
      const slug = generateSlug(formData.name)

      // Комбинируем дату и время
      const eventDateTime = `${formData.eventDate}T${formData.eventTime}:00`

      const locationsPayload = formData.locations
        .map(loc => ({
          name: {
            ...(loc.nameRu.trim() ? { ru: loc.nameRu.trim() } : {}),
            ...(loc.nameKy.trim() ? { ky: loc.nameKy.trim() } : {}),
            ...(loc.nameEn.trim() ? { en: loc.nameEn.trim() } : {})
          },
          latitude: loc.latitude ? parseFloat(loc.latitude) : null,
          longitude: loc.longitude ? parseFloat(loc.longitude) : null,
          regionId: loc.regionId ? Number(loc.regionId) : null
        }))
        .filter(loc =>
          Object.keys(loc.name).length > 0
          || loc.latitude != null
          || loc.longitude != null
          || loc.regionId != null
        )

      const payload = {
        slug,
        name: formData.name,
        description: formData.description,
        locations: locationsPayload,
        eventDate: eventDateTime,
        collectFrom: formData.collectFrom || null,
        collectTo: formData.collectTo || null,
        targetTrees: formData.targetTrees ? parseInt(formData.targetTrees) : null,
        targetAmount: formData.targetAmount ? parseInt(formData.targetAmount) : null,
        status: formData.status,
        isActiveDefault: formData.isActiveDefault,
        participantsCount: formData.participantsCount ? parseInt(formData.participantsCount) : null,
        areaHa: formData.areaHa ? parseFloat(formData.areaHa) : null,
        seedlingTypes: formData.seedlingTypes
          ? formData.seedlingTypes
              .split('\n')
              .map(l => l.trim())
              .filter(Boolean)
              .map(line => {
                const m = line.match(/^(.+?)\s*[—-]\s*(\d+)$/)
                if (m) return { name: { ru: m[1].trim() }, count: parseInt(m[2]) }
                return { name: { ru: line } }
              })
          : []
      }

      if (id) {
        await adminApi.put(`/api/admin/events/${id}`, payload)
        setNotification({ message: 'Событие успешно обновлено', type: 'success' })
      } else {
        await adminApi.post('/api/admin/events', payload)
        setNotification({ message: 'Событие успешно создано', type: 'success' })
      }

      setTimeout(() => {
        navigate('/admin/events')
      }, 1500)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка сохранения события')
        : 'Ошибка сохранения события'
      setError(errorMessage)
      setNotification({ message: errorMessage, type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Media handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024
      const tooLarge = selectedFiles.filter(file => file.size > maxSize)

      if (tooLarge.length > 0) {
        setNotification({
          message: `Файлы превышают максимальный размер ${MAX_FILE_SIZE_MB}MB: ${tooLarge.map(f => f.name).join(', ')}`,
          type: 'error'
        })
        return
      }

      setNewFiles(prev => [...prev, ...selectedFiles])
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    )

    if (droppedFiles.length > 0) {
      const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024
      const tooLarge = droppedFiles.filter(file => file.size > maxSize)

      if (tooLarge.length > 0) {
        setNotification({
          message: `Файлы превышают максимальный размер ${MAX_FILE_SIZE_MB}MB: ${tooLarge.map(f => f.name).join(', ')}`,
          type: 'error'
        })
        return
      }

      setNewFiles(prev => [...prev, ...droppedFiles])
    }
  }

  const removeNewFile = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleNewFileDragStart = (index: number) => {
    setDraggedFileIndex(index)
  }

  const handleNewFileDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedFileIndex === null || draggedFileIndex === index) return

    const files = [...newFiles]
    const draggedFile = files[draggedFileIndex]

    files.splice(draggedFileIndex, 1)
    files.splice(index, 0, draggedFile)

    setNewFiles(files)
    setDraggedFileIndex(index)
  }

  const handleNewFileDragEnd = () => {
    setDraggedFileIndex(null)
  }

  const addVideoUrlField = () => {
    setVideoUrls(prev => [...prev, ''])
  }

  const updateVideoUrl = (index: number, value: string) => {
    setVideoUrls(prev => prev.map((url, i) => i === index ? value : url))
  }

  const removeVideoUrl = (index: number) => {
    if (videoUrls.length > 1) {
      setVideoUrls(prev => prev.filter((_, i) => i !== index))
    } else {
      setVideoUrls([''])
    }
  }

  const deleteExistingMedia = (mediaId: number) => {
    setConfirmDelete({show: true, id: mediaId})
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete.id) return

    try {
      await adminApi.delete(`/api/admin/media/${confirmDelete.id}`)
      setExistingMedia(prev => prev.filter(m => m.id !== confirmDelete.id))
      setNotification({ message: 'Медиа файл удален', type: 'success' })
    } catch (err) {
      setNotification({ message: 'Ошибка при удалении', type: 'error' })
    } finally {
      setConfirmDelete({show: false, id: null})
    }
  }

  const handleMediaDragStart = (index: number) => {
    setDraggedMediaIndex(index)
  }

  const handleMediaDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedMediaIndex === null || draggedMediaIndex === index) return

    const media = [...existingMedia]
    const draggedItem = media[draggedMediaIndex]

    media.splice(draggedMediaIndex, 1)
    media.splice(index, 0, draggedItem)

    setExistingMedia(media)
    setDraggedMediaIndex(index)
  }

  const handleMediaDragEnd = async () => {
    setDraggedMediaIndex(null)
    // Auto-save order after drag
    if (id) {
      try {
        const mediaIds = existingMedia.map(m => m.id)

        await adminApi.put(
          '/api/admin/media/reorder',
          { mediaIds }
        )
      } catch (err) {
        setNotification({ message: 'Ошибка при сохранении порядка', type: 'error' })
      }
    }
  }

  const saveMediaOrder = async () => {
    try {
      const mediaIds = existingMedia.map(m => m.id)

      await adminApi.put(
        '/api/admin/media/reorder',
        { mediaIds }
      )

      setNotification({ message: 'Порядок сохранен', type: 'success' })
    } catch (err) {
      setNotification({ message: 'Ошибка при сохранении порядка', type: 'error' })
    }
  }

  const uploadNewMedia = async () => {
    if (!id) {
      setNotification({ message: 'Сначала сохраните событие', type: 'error' })
      return
    }

    const hasFiles = newFiles.length > 0
    const hasVideos = videoUrls.some(url => url.trim() !== '')

    if (!hasFiles && !hasVideos) {
      setNotification({ message: 'Добавьте файлы или URL видео', type: 'error' })
      return
    }

    setLoading(true)
    try {
      const formData = new FormData()

      newFiles.forEach(file => {
        formData.append('files', file)
      })

      const validVideoUrls = videoUrls.filter(url => url.trim() !== '')
      validVideoUrls.forEach(url => {
        formData.append('videoUrls', url.trim())
      })

      await adminApi.post(
        `/api/admin/events/${id}/media`,
        formData
      )

      setNotification({ message: 'Медиа успешно загружено', type: 'success' })
      setNewFiles([])
      setVideoUrls([''])
      setShowUploadForm(false)

      // Reload event to get updated media
      fetchEvent(id)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при загрузке')
        : 'Ошибка при загрузке'
      setNotification({ message: errorMessage, type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof EventFormData, value: string | boolean) => {
    setFormData({ ...formData, [field]: value })
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' })
    }
  }

  const getMediaThumbnail = (item: MediaFile) => {
    if (item.type === 'image') {
      return `${API_URL}${item.url}`
    }
    if (item.type === 'video' && item.thumbnail) {
      return item.thumbnail
    }
    return null
  }

  const isReadOnly = userRole === 'manager'

  if (loading && id) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="loading-spinner w-10 h-10"></div>
      </div>
    )
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title">
            {userRole === 'manager' && id ? 'Просмотр события' : (id ? 'Редактировать событие' : 'Новое событие')}
          </h1>
          <p className="page-subtitle">
            {userRole === 'manager' ? 'Детальная информация о событии' : 'Заполните информацию о событии'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/events')}
            className="btn-secondary"
            disabled={loading}
          >
            {userRole === 'manager' ? 'Назад' : 'Отменить'}
          </button>
          {userRole === 'admin' && (
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? 'Сохранение...' : (id ? 'Сохранить изменения' : 'Создать событие')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-5">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-form">
          {/* Основная информация */}
          <div className="form-section">
            <h3 className="form-section-title">
              Основная информация
            </h3>

            <div className="form-group mb-5">
              <label htmlFor="eventName">
                Название <span className="text-red-500">*</span>
              </label>
              <input
                id="eventName"
                type="text"
                name="eventName"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="Весенняя высадка деревьев"
                autoComplete="off"
                className={formErrors.name ? 'border-red-500' : ''}
                disabled={isReadOnly}
              />
              {formErrors.name && (
                <div className="field-error">
                  {formErrors.name}
                </div>
              )}
            </div>

            <div className="form-group mb-5">
              <label htmlFor="eventDescription">
                Описание <span className="text-red-500">*</span>
              </label>
              <textarea
                id="eventDescription"
                name="eventDescription"
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                rows={4}
                placeholder="Подробное описание события, его цели и планируемые мероприятия"
                autoComplete="off"
                className={`resize-y ${formErrors.description ? 'border-red-500' : ''}`}
                disabled={isReadOnly}
              />
              {formErrors.description && (
                <div className="field-error">
                  {formErrors.description}
                </div>
              )}
            </div>

            <div
              className="grid gap-4"
              // dynamic: auto-fit grid without Tailwind's fixed cols API
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
            >
              <div className="form-group">
                <label htmlFor="eventDate">
                  Дата события <span className="text-red-500">*</span>
                </label>
                <input
                  id="eventDate"
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={e => handleInputChange('eventDate', e.target.value)}
                  autoComplete="off"
                  className={formErrors.eventDate ? 'border-red-500' : ''}
                  disabled={isReadOnly}
                />
                {formErrors.eventDate && (
                  <div className="field-error">
                    {formErrors.eventDate}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="eventTime">
                  Время события <span className="text-red-500">*</span>
                </label>
                <input
                  id="eventTime"
                  type="time"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={e => handleInputChange('eventTime', e.target.value)}
                  autoComplete="off"
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label htmlFor="collectFrom">Сбор средств с</label>
                <input
                  id="collectFrom"
                  type="date"
                  name="collectFrom"
                  value={formData.collectFrom}
                  onChange={e => setFormData({ ...formData, collectFrom: e.target.value })}
                  autoComplete="off"
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label htmlFor="collectTo">Сбор средств до</label>
                <input
                  id="collectTo"
                  type="date"
                  name="collectTo"
                  value={formData.collectTo}
                  onChange={e => setFormData({ ...formData, collectTo: e.target.value })}
                  autoComplete="off"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {/* Цели и настройки */}
          <div className="form-section">
            <h3 className="form-section-title">
              Цели и настройки
            </h3>

            <div
              className="grid gap-4 mb-5"
              // dynamic: auto-fit grid
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
            >
              <div className="form-group">
                <label htmlFor="targetTrees">Целевое кол-во деревьев</label>
                <input
                  id="targetTrees"
                  type="number"
                  name="targetTrees"
                  value={formData.targetTrees}
                  onChange={e => setFormData({ ...formData, targetTrees: e.target.value })}
                  placeholder="1000"
                  autoComplete="off"
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label htmlFor="targetAmount">Целевая сумма (KGS)</label>
                <input
                  id="targetAmount"
                  type="number"
                  name="targetAmount"
                  value={formData.targetAmount}
                  onChange={e => setFormData({ ...formData, targetAmount: e.target.value })}
                  placeholder="100000"
                  autoComplete="off"
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventStatus">
                  Статус <span className="text-red-500">*</span>
                </label>
                <select
                  id="eventStatus"
                  name="eventStatus"
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value as 'planned' | 'active' | 'finished' })}
                  autoComplete="off"
                  required
                  disabled={isReadOnly}
                >
                  <option value="planned">Запланировано</option>
                  <option value="active">Активно</option>
                  <option value="finished">Завершено</option>
                </select>
              </div>
            </div>

            <label className={`admin-checkbox-row ${isReadOnly || (!id && totalEventsCount === 0) ? 'is-disabled' : ''}`}>
              <input
                id="isActiveDefault"
                type="checkbox"
                name="isActiveDefault"
                checked={formData.isActiveDefault}
                disabled={isReadOnly || (!id && totalEventsCount === 0)}
                onChange={() => {
                  if (formData.isActiveDefault) {
                    setNotification({
                      message: 'Чтобы убрать событие по умолчанию, сначала установите галочку на другом событии',
                      type: 'error'
                    })
                    return
                  }
                  setFormData({ ...formData, isActiveDefault: !formData.isActiveDefault })
                }}
                autoComplete="off"
                className="admin-checkbox"
              />
              <div>
                <div className="admin-checkbox-title">Событие по умолчанию</div>
                <div className="admin-checkbox-hint">
                  Все оплаты на сайте будут автоматически связаны с этим событием.
                  Только одно может быть активным одновременно.
                  {!id && totalEventsCount === 0 && (
                    <> Это первое событие — оно станет активным автоматически.</>
                  )}
                </div>
              </div>
            </label>

          </div>

          {/* Локации */}
          <div className="form-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="form-section-title" style={{ margin: 0, padding: 0, border: 0 }}>
                Локации
              </h3>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={addLocation}
                  className="btn-secondary text-[13px] px-3 py-1.5"
                >
                  + Добавить локацию
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Событие может проходить в нескольких местах одновременно. Порядок можно менять
              перетаскиванием — на публичной части сначала показывается первая локация.
              Донаты идут в выбранный пользователем регион или в общий пул «Кыргызстан».
            </p>

            {formData.locations.length === 0 && (
              <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg py-6 text-center">
                Локаций пока нет. Добавьте хотя бы одну, если хотите показать на карте.
              </div>
            )}

            {formData.locations.map((loc, idx) => (
              <div
                key={loc.key}
                draggable={!isReadOnly}
                onDragStart={() => handleLocationDragStart(idx)}
                onDragOver={(e) => handleLocationDragOver(e, idx)}
                onDragEnd={handleLocationDragEnd}
                className={`border rounded-lg p-4 mb-4 bg-white transition-all ${
                  draggedLocationIdx === idx ? 'opacity-40 border-green-500' : 'border-gray-200'
                }`}
                style={{ cursor: isReadOnly ? 'default' : 'move' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <span className="text-gray-400">⋮⋮</span>
                    Локация {idx + 1}
                    {idx === 0 && (
                      <span className="text-[11px] font-normal text-green-700 bg-green-50 rounded-full px-2 py-0.5 ml-1">
                        Главная
                      </span>
                    )}
                  </div>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => removeLocation(idx)}
                      className="text-[13px] text-red-600 hover:text-red-800"
                    >
                      Удалить
                    </button>
                  )}
                </div>

                {/* Названия ru/ky/en */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="form-label text-xs">Название (ру)</label>
                    <input
                      type="text"
                      value={loc.nameRu}
                      onChange={(e) => updateLocation(idx, { nameRu: e.target.value })}
                      placeholder="Например: Парк Ата-Тюрк, Бишкек"
                      disabled={isReadOnly}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label text-xs">Название (ky)</label>
                    <input
                      type="text"
                      value={loc.nameKy}
                      onChange={(e) => updateLocation(idx, { nameKy: e.target.value })}
                      disabled={isReadOnly}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label text-xs">Название (en)</label>
                    <input
                      type="text"
                      value={loc.nameEn}
                      onChange={(e) => updateLocation(idx, { nameEn: e.target.value })}
                      disabled={isReadOnly}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Регион */}
                <div className="form-group mb-3">
                  <label className="form-label text-xs">Регион</label>
                  <select
                    className="form-input"
                    value={loc.regionId}
                    onChange={(e) => updateLocation(idx, { regionId: e.target.value })}
                    disabled={isReadOnly}
                  >
                    <option value="">Кыргызстан (без привязки к области)</option>
                    {regions.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name?.ru || r.name?.en || r.code}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Карта */}
                <LocationPickerWithModal
                  latitude={loc.latitude}
                  longitude={loc.longitude}
                  locationName={loc.nameRu}
                  hideNameInput={true}
                  onLocationChange={(lat, lng, name) => {
                    const code = findRegionCodeByCoords(lat, lng)
                    const auto = code ? regions.find(r => r.code === code) : undefined
                    updateLocation(idx, {
                      latitude: lat.toString(),
                      longitude: lng.toString(),
                      nameRu: name || loc.nameRu,
                      regionId: auto ? String(auto.id) : loc.regionId
                    })
                  }}
                  onLocationNameChange={(name) => updateLocation(idx, { nameRu: name })}
                  disabled={isReadOnly}
                  hasError={false}
                />
              </div>
            ))}
          </div>

          {/* Партнёры фестиваля */}
          <div className="form-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="form-section-title" style={{ margin: 0, padding: 0, border: 0 }}>
                Партнёры фестиваля
              </h3>
              {id && !isReadOnly && (
                <button
                  type="button"
                  onClick={openPartnerPicker}
                  className="btn-secondary text-[13px] px-3 py-1.5"
                  disabled={partnersBusy}
                >
                  Выбрать партнёров
                </button>
              )}
            </div>
            {!id && (
              <p className="text-xs text-gray-500">Партнёров можно добавить после сохранения события.</p>
            )}
            {id && attached.length === 0 && (
              <p className="text-xs text-gray-500">Партнёры пока не привязаны.</p>
            )}
            {attached.length > 0 && (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
              >
                {attached.map(a => (
                  <div
                    key={a.id}
                    className="relative flex flex-col items-center justify-between p-2 border border-gray-200 rounded-md bg-white min-h-[80px]"
                  >
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => detachPartner(a.id)}
                        disabled={partnersBusy}
                        className="absolute top-1 right-1 bg-transparent border-0 cursor-pointer text-gray-400 hover:text-red-500 text-sm leading-none"
                        title="Отвязать"
                      >
                        ×
                      </button>
                    )}
                    {a.logo_url ? (
                      <img
                        src={a.logo_url}
                        alt={a.name}
                        className="w-9 h-9 object-contain"
                      />
                    ) : (
                      <div className="w-9 h-9 bg-gray-100 rounded" />
                    )}
                    <div className="text-[11px] text-center w-full truncate mt-1">
                      {a.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Итоги фестиваля (заполняется после проведения) */}
          <div className="form-section">
            <h3 className="form-section-title">Итоги фестиваля</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Участников</label>
                <input
                  type="number"
                  min={0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.participantsCount}
                  onChange={e => setFormData({ ...formData, participantsCount: e.target.value })}
                  disabled={isReadOnly}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Площадь, га</label>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.areaHa}
                  onChange={e => setFormData({ ...formData, areaHa: e.target.value })}
                  disabled={isReadOnly}
                  placeholder="0.0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Виды саженцев (по одному в строке, формат: <code>Название — количество</code>)
              </label>
              <textarea
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                value={formData.seedlingTypes}
                onChange={e => setFormData({ ...formData, seedlingTypes: e.target.value })}
                disabled={isReadOnly}
                placeholder={'Ель тянь-шаньская — 4000\nКарагач — 3000\nБерёза — 2000'}
              />
              <div className="text-xs text-gray-500 mt-1">
                Количество опционально: строка «Дуб» без «— 100» тоже валидна.
              </div>
            </div>
          </div>

          {/* Медиа файлы */}
          {!id && (
            <div className="form-section" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
              <div className="text-sm text-[#0050b3]">
                💡 После сохранения события вы сможете добавить изображения и видео
              </div>
            </div>
          )}
          {id && (
            <div className="form-section">
              <div className="flex items-center justify-between mb-5">
                <h3 className="form-section-title" style={{ margin: 0, padding: 0, border: 0 }}>
                  Медиа файлы
                </h3>
                {existingMedia.length > 0 && !showUploadForm && (
                  <button
                    type="button"
                    onClick={() => setShowUploadForm(true)}
                    className="btn-primary text-[13px] px-3 py-1.5"
                  >
                    Загрузить изображения
                  </button>
                )}
              </div>

              {/* Empty State */}
              {existingMedia.length === 0 && !showUploadForm && (
                <div className="text-center px-5 py-[60px] bg-[#fafafa] rounded-lg border border-dashed border-[#d9d9d9]">
                  {React.createElement(MdPhotoLibrary as any, { size: 64, className: 'block mx-auto mb-4 text-black/25' })}
                  <div className="text-sm text-black/45 mb-4">
                    Медиа файлы не загружены
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowUploadForm(true)}
                    className="btn-primary"
                  >
                    Загрузить изображения
                  </button>
                </div>
              )}

              {/* Existing Media */}
              {existingMedia.length > 0 && (
                <div className="mb-6">
                  <div className="text-sm font-medium text-[#1890ff] mb-3">
                    Загруженные файлы: {existingMedia.length}
                  </div>
                  <div
                    className="grid gap-3"
                    // dynamic: auto-fill grid
                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))' }}
                  >
                    {existingMedia.map((item, index) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={() => handleMediaDragStart(index)}
                        onDragOver={(e) => handleMediaDragOver(e, index)}
                        onDragEnd={handleMediaDragEnd}
                        className={`relative aspect-square rounded-lg overflow-hidden transition-all ${
                          draggedMediaIndex === index ? 'border-2 border-dashed border-[#1890ff]' : 'border border-[#d9d9d9]'
                        }`}
                      >
                        <div
                          onClick={() => {
                            if (item.type === 'image') {
                              setModalImage(getMediaThumbnail(item))
                            } else if (item.type === 'video') {
                              window.open(item.url, '_blank')
                            }
                          }}
                          className="w-full h-full cursor-pointer relative"
                        >
                          {getMediaThumbnail(item) ? (
                            <>
                              <img
                                src={getMediaThumbnail(item)!}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                              {item.type === 'video' && (
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center pointer-events-none">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
                                    <path d="M4 2 L4 14 L12 8 Z" />
                                  </svg>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#f5f5f5] text-gray-500">
                              {item.type === 'video' ? '🎥' : '📷'}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteExistingMedia(item.id)
                          }}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/50 border-0 text-[#ff4d4f] cursor-pointer text-base font-bold flex items-center justify-center p-0 transition-transform hover:scale-[.85]"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Form */}
              {showUploadForm && (
                <>
                  {/* Video URLs */}
                  <div className="mb-5">
                <label className="block mb-2 text-sm text-black/85">
                  URL видео (YouTube)
                </label>
                {videoUrls.map((url, index) => (
                  <div key={`video-${index}-${url}`} className="flex gap-2 mb-2">
                    <input
                      id={`eventVideoUrl${index}`}
                      type="url"
                      name={`eventVideoUrl${index}`}
                      value={url}
                      onChange={(e) => updateVideoUrl(index, e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="form-control flex-1"
                      autoComplete="url"
                    />
                    {videoUrls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVideoUrl(index)}
                        className="btn-danger px-3 py-2"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addVideoUrlField}
                  className="bg-transparent border-0 text-[#1890ff] cursor-pointer text-sm p-0"
                >
                  + Добавить еще URL
                </button>
              </div>

              {/* Drag & Drop Zone for Images */}
              <div className="mb-5">
                <label className="block mb-2 text-sm text-black/85">
                  Изображения
                </label>
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  className={`rounded-lg p-10 text-center transition-all cursor-pointer border-2 border-dashed ${
                    isDragging ? 'border-[#1890ff] bg-[#f0f7ff]' : 'border-[#d9d9d9] bg-[#fafafa]'
                  }`}
                  onClick={() => document.getElementById('media-file-input')?.click()}
                >
                  {React.createElement(MdCloudUpload as any, { size: 48, className: 'block mx-auto mb-3 text-[#1890ff]' })}
                  <div className="text-sm text-black/85 mb-1">
                    Перетащите изображения сюда или нажмите для выбора
                  </div>
                  <div className="text-xs text-black/45">
                    Максимальный размер файла: {MAX_FILE_SIZE_MB} MB
                  </div>
                  <input
                    id="media-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Selected Files */}
                {newFiles.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium mb-3 text-black/85">
                      Новые файлы: {newFiles.length}
                    </div>
                    <div
                      className="grid gap-3"
                      // dynamic: auto-fill grid
                      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))' }}
                    >
                      {newFiles.map((file, index) => (
                        <div
                          key={`file-${file.name}-${file.size}-${index}`}
                          draggable
                          onDragStart={() => handleNewFileDragStart(index)}
                          onDragOver={(e) => handleNewFileDragOver(e, index)}
                          onDragEnd={handleNewFileDragEnd}
                          className={`relative aspect-square rounded-lg overflow-hidden transition-all ${
                            draggedFileIndex === index ? 'border-2 border-dashed border-[#1890ff]' : 'border border-[#d9d9d9]'
                          }`}
                        >
                          <div
                            onClick={() => setModalImage(fileUrlMap.get(file) || '')}
                            className="w-full h-full cursor-pointer"
                          >
                            <img
                              src={fileUrlMap.get(file) || ''}
                              alt={file.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeNewFile(index)
                            }}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/50 border-0 text-[#ff4d4f] cursor-pointer text-base font-bold flex items-center justify-center p-0 transition-transform hover:scale-[.85]"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

                  {(newFiles.length > 0 || videoUrls.some(url => url.trim() !== '')) && (
                    <button
                      type="button"
                      onClick={uploadNewMedia}
                      className="btn-primary w-full"
                      disabled={loading}
                    >
                      {loading ? 'Загрузка...' : 'Загрузить медиа'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

        </form>

        {/* Image Modal */}
        {modalImage && (
          <div
            className="fixed inset-0 bg-black/85 flex items-center justify-center z-[9999]"
            onClick={() => setModalImage(null)}
          >
            <div
              className="relative w-[80vh] h-[80vh] bg-black rounded-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={modalImage}
                alt="Full size"
                className="w-full h-full object-contain"
              />
              <button
                onClick={() => setModalImage(null)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 border-0 text-black cursor-pointer flex items-center justify-center text-2xl transition-all hover:bg-white hover:scale-110"
              >
                {React.createElement(MdClose as any, { size: 24 })}
              </button>
            </div>
          </div>
        )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить этот медиа файл? Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({show: false, id: null})}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />

      {/* Попап multi-select партнёров */}
      {partnerPickerOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.stopPropagation()
              setPartnerPickerOpen(false)
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white rounded-lg w-[90%] max-w-[500px] max-h-[80vh] shadow-md flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold m-0">
                Партнёры фестиваля ({partnerPickerTempIds.size})
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setPartnerPickerOpen(false)
                }}
                className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <input
                type="text"
                value={partnerPickerSearch}
                onChange={(e) => setPartnerPickerSearch(e.target.value)}
                placeholder="Поиск…"
                className="form-control"
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {allCompanies
                .filter(c => c.name.toLowerCase().includes(partnerPickerSearch.toLowerCase()))
                .map(c => {
                  const isChecked = partnerPickerTempIds.has(c.id)
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-5 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          setPartnerPickerTempIds(prev => {
                            const next = new Set(prev)
                            if (next.has(c.id)) next.delete(c.id)
                            else next.add(c.id)
                            return next
                          })
                        }}
                        className="w-4 h-4 flex-shrink-0"
                      />
                      {c.logo_url ? (
                        <img src={c.logo_url} alt="" className="w-9 h-9 object-contain flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 bg-gray-100 rounded flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate">{c.name}</span>
                    </label>
                  )
                })}
              {allCompanies.filter(c => c.name.toLowerCase().includes(partnerPickerSearch.toLowerCase())).length === 0 && (
                <div className="px-5 py-4 text-sm text-gray-500 text-center">Не найдено</div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-between gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setPartnerPickerOpen(false)
                }}
                className="btn-secondary"
                disabled={partnersBusy}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  savePartnerPicker()
                }}
                className="btn-primary"
                disabled={partnersBusy}
              >
                {partnersBusy ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
